<div class="updox-attachments-panel">

  <!-- Initial load spinner (first fetch only) -->
  <div *ngIf="isLoading" class="text-center text-muted p-sm">
    <i class="fa fa-spinner fa-spin mr-sm"></i> Loading attachments...
  </div>

  <!-- Empty (no attachments at all, not loading) -->
  <div *ngIf="!isLoading && totalCount === 0" class="text-muted p-sm">
    <i class="fa fa-paperclip mr-sm"></i> No attachments.
  </div>

  <!-- Pending processing — normal state (job still running, within timeout) -->
  <div class="updox-attachment-pending" *ngIf="pending.length > 0 && !pollTimedOut">
    <i class="fa fa-spinner fa-spin mr-sm text-warning"></i>
    <strong>Processing {{ pending.length }} attachment(s)…</strong>
    <ul class="updox-pending-list">
      <li *ngFor="let p of pending">
        <div class="updox-attachment-pending-row">
          <span>
            <i class="fa fa-file-o mr-sm text-muted"></i>{{ p.file_name }}
            <span class="label label-info ml-sm" *ngIf="p.is_ccda">C-CDA</span>
          </span>
          <!-- Pending C-CDA can still be parsed from raw metadata -->
          <a class="btn btn-xs btn-info btn-outline ml-sm"
             *ngIf="p.is_ccda"
             (click)="openCcdaPreviewForPending(p)"
             [class.active]="activeCcdaPendingIndex === p.index"
             title="Preview clinical data while processing">
            <i class="fa fa-file-text-o mr-sm"></i>Clinical Summary
          </a>
        </div>
      </li>
    </ul>
  </div>

  <!-- Timed out — job never finished after 48 s -->
  <div class="updox-attachment-timeout" *ngIf="pollTimedOut && pending.length > 0">
    <i class="fa fa-exclamation-triangle mr-sm text-warning"></i>
    <span>Attachments are still being processed by the server.</span>
    <a class="btn btn-xs btn-default btn-outline ml-sm" (click)="retryPolling()">
      <i class="fa fa-refresh mr-sm"></i>Check Again
    </a>
    <ul class="updox-pending-list mt-sm">
      <li *ngFor="let p of pending">
        <div class="updox-attachment-pending-row">
          <span>
            <i class="fa fa-file-o mr-sm text-muted"></i>{{ p.file_name }}
            <span class="label label-info ml-sm" *ngIf="p.is_ccda">C-CDA</span>
          </span>
          <a class="btn btn-xs btn-info btn-outline ml-sm"
             *ngIf="p.is_ccda"
             (click)="openCcdaPreviewForPending(p)"
             [class.active]="activeCcdaPendingIndex === p.index">
            <i class="fa fa-file-text-o mr-sm"></i>Clinical Summary
          </a>
        </div>
      </li>
    </ul>
  </div>

  <!-- Processed attachment rows -->
  <div class="updox-attachment-item" *ngFor="let att of attachments">

    <!-- Row -->
    <div class="updox-attachment-row">
      <div class="updox-attachment-info col-lg-7 col-sm-12">
        <i class="fa {{ fileIcon(att.document_content_type) }} fs15 mr-sm"></i>
        <span class="fw-500 fs13">{{ att.document_file_name }}</span>

        <span class="label label-success ml-sm" style="padding:0.3em 0.6em 0.3em;" *ngIf="att.added_to_medical_chart" title="This attachment has been added to the patient chart">
          <i class="fa fa-check-circle mr-sm"></i>Added to Chart
        </span>
      </div>
      <div class="updox-attachment-actions col-lg-5 col-sm-12">
        <a class="btn btn-xs btn-default btn-outline mr-sm" (click)="viewDocument(att)" title="Open in new tab">
          <i class="fa fa-external-link mr-sm"></i>View
        </a>

        <a class="btn btn-xs btn-secondary btn-outline mr-sm summary-btn "


           *ngIf="att.is_ccda && !readOnly"
           (click)="activeCcdaAttachment?.id === att.id ? closeCcdaPreview() : openCcdaPreview(att)"
           [class.active]="activeCcdaAttachment?.id === att.id"
           title="View structured clinical data — including the document's own patient info, useful for confirming who to search for or add above">
          <i class="fa fa-file-text-o mr-sm"></i>Clinical Summary
        </a>
        <a class="btn btn-xs btn-success btn-outline"
           *ngIf="!readOnly && !att.added_to_medical_chart"
           (click)="activeAddToChartId === att.id ? closeAddToChart() : openAddToChart(att)"
           [class.active]="activeAddToChartId === att.id"
           [class.disabled]="!message.patient_id"
           [title]="message.patient_id ? 'Add this document to patient chart' : 'Link a patient above first'">
          <i class="fa fa-plus-circle mr-sm"></i>Add to Chart
        </a>
        <a class="btn btn-xs btn-danger btn-outline"
           *ngIf="!readOnly && att.added_to_medical_chart"
           (click)="activeRemoveFromChartId === att.id ? cancelRemoveFromChart() : openRemoveFromChart(att)"
           [class.active]="activeRemoveFromChartId === att.id"
           title="Remove this attachment from the patient chart">
          <i class="fa fa-minus-circle mr-sm"></i>Remove from Chart
        </a>
      </div>
    </div>

    <!-- Inline Remove-from-Chart confirm -->
    <div class="alert alert-danger p-sm m-sm" *ngIf="!readOnly && activeRemoveFromChartId === att.id">
      Remove this document from the patient's medical chart? This cannot be undone.
      <div class="pull-right">
        <a class="btn btn-xs btn-danger mr-sm" (click)="confirmRemoveFromChart(att)">
          <i class="fa fa-spinner fa-spin mr-xs" *ngIf="isRemovingFromChart"></i>Yes, Remove
        </a>
        <a class="btn btn-xs btn-default" (click)="cancelRemoveFromChart()">Cancel</a>
      </div>
    </div>

    <!-- Inline Add-to-Chart form — patient is resolved once for the whole
         message (see message-detail's sticky patient bar), so this form only
         ever files the document under whoever's already linked there. -->
    <div class="updox-add-to-chart-form" *ngIf="!readOnly && activeAddToChartId === att.id">

      <div class="updox-atc-section-label add-to-chart-label p">
        <i class="fa fa-user mr-lg text-primary"></i>
        <span class="fw-500">Adding to chart for:</span>
        <span class="fw-500 text-secondary ml-sm">{{ message.patient_name }}</span>
      </div>

      <!-- Document details -->
      <div class="updox-atc-details mb-lg add-to-chart-label p">
        <div class="updox-atc-section-label mb-lg">
          <i class="fa fa-file-text mr-sm text-info"></i>
          <span class="fw-500">Document Details</span>
        </div>
        <div class="row">
          <div class="col-lg-6 col-md-12 col-sm-12 form-group mb-sm">
            <label class="field-label">Document Name</label>
            <input type="text" class="form-control input-sm" [(ngModel)]="addToChartName"
                   placeholder="Defaults to message subject if left blank" />
          </div>
          <div class="col-lg-3 col-md-6 col-sm-12 form-group mb-sm">
            <label class="field-label">Document Type</label>
            <select class="form-control input-sm" [(ngModel)]="addToChartDocTypeId">
              <option [ngValue]="null">Select Type</option>
              <option *ngFor="let dt of documentTypes" [ngValue]="dt.id">{{ dt.name }}</option>
            </select>
          </div>
          <div class="col-lg-3 col-md-6 col-sm-12 form-group mb-sm">
            <label class="field-label">Document Date</label>
            <input type="date" class="form-control input-sm" [(ngModel)]="addToChartDate" />
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="updox-atc-footer">
        <button type="button" class="btn btn-sm btn-default mr-sm" (click)="closeAddToChart()">
          Cancel
        </button>
        <button type="button" class="btn btn-sm btn-success"
                (click)="submitAddToChart()"
                [disabled]="isAddingToChart">
          <i class="fa fa-spinner fa-spin mr-sm" *ngIf="isAddingToChart"></i>
          <i class="fa fa-check mr-sm" *ngIf="!isAddingToChart"></i>
          {{ isAddingToChart ? 'Saving to Chart…' : 'Save to Chart' }}
        </button>
      </div>
    </div>
  </div>
  <!-- C-CDA Preview panel — processed attachment -->
  <!-- The following code need to be uncomment +HSA+ -->
  <app-updox-ccda-preview
    *ngIf="activeCcdaAttachment"
    [message]="message"
    [attachment]="activeCcdaAttachment"
    (close)="closeCcdaPreview()"
    (chartAdded)="onChartAdded()">
  </app-updox-ccda-preview>

  <!-- C-CDA Preview panel — pending attachment (parsed from raw metadata) -->
  <app-updox-ccda-preview
    *ngIf="activeCcdaPendingIndex != null"
    [message]="message"
    [metadataIndex]="activeCcdaPendingIndex"
    (close)="closeCcdaPreview()"
    (chartAdded)="onChartAdded()">
  </app-updox-ccda-preview>

</div>
